/**
 * Browser-local timezone helpers. We render every time field in the operator's local timezone via
 * `Intl.DateTimeFormat`, which already knows the IANA zone the OS is configured for. The BFF stores
 * everything as UTC `DateTimeOffset`s — the conversion is purely a display concern.
 *
 * If we ever need to know an *employee's* timezone (rather than the operator's), we can fetch
 * `/me/mailboxSettings.timeZone` from Microsoft Graph and pass it explicitly to these helpers.
 */

/** IANA zone the browser is running in, e.g. `"America/New_York"`. */
export function getLocalTimezoneId(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Short timezone abbreviation for the current moment (DST-aware), e.g. `"EDT"`. */
export function getTimezoneAbbreviation(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: getLocalTimezoneId(),
    timeZoneName: 'short'
  }).formatToParts(date);
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

/** UTC offset string for the current moment, e.g. `"UTC−4"`. */
export function getTimezoneOffsetLabel(date: Date = new Date()): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '\u2212';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return minutes === 0 ? `UTC${sign}${hours}` : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

const TIMEZONE_DISPLAY_NAMES: Record<string, string> = {
  'America/Detroit': 'Eastern Time',
  'America/Indiana/Indianapolis': 'Eastern Time',
  'America/Indianapolis': 'Eastern Time',
  'America/Kentucky/Louisville': 'Eastern Time',
  'America/Kentucky/Monticello': 'Eastern Time',
  'America/New_York': 'Eastern Time'
};

/** Combined human-friendly label, e.g. `"Eastern Time (EDT, UTC−4)"`. */
export function getTimezoneLabel(): string {
  const id = getLocalTimezoneId();
  const abbr = getTimezoneAbbreviation();
  const offset = getTimezoneOffsetLabel();
  const inside = abbr ? `${abbr}, ${offset}` : offset;
  return `${TIMEZONE_DISPLAY_NAMES[id] ?? id} (${inside})`;
}

/** Render an ISO string in the operator's local timezone, e.g. `"Jun 1, 2026, 9:00 AM"`. */
export function formatLocalDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

/** Render an ISO string as a date-only label in the operator's local zone, e.g. `"Jun 1, 2026"`. */
export function formatLocalDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Render an all-day UTC-midnight value by its UTC date, avoiding local-zone day shifts. */
export function formatAllDayUtcDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
}

/** Convert an all-day UTC-midnight value to a local Date with the same calendar day for Scheduler widgets. */
export function toLocalAllDayDate(value: string | Date | null | undefined): Date {
  const d = value instanceof Date ? value : new Date(value ?? '');
  if (Number.isNaN(d.getTime())) return new Date('');
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * DevExtreme Scheduler renders an all-day appointment across every day cell its start/end span
 * touches. The BFF stores all-day end as the exclusive next-day UTC midnight (Graph convention),
 * so we convert to the inclusive last day's end (23:59:59.999 local) to avoid spilling onto an
 * extra trailing cell.
 */
export function toLocalAllDaySchedulerEndDate(value: string | Date | null | undefined): Date {
  const d = value instanceof Date ? value : new Date(value ?? '');
  if (Number.isNaN(d.getTime())) return new Date('');
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1, 23, 59, 59, 999);
}

/**
 * Render a start/end window. All-day events render date-only and inclusively (the BFF stores end as
 * the exclusive next-day midnight per Graph convention, so we subtract a day for display).
 */
export function formatLocalRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  isAllDay: boolean
): string {
  if (!startIso || !endIso) return '';
  if (!isAllDay) {
    return `${formatLocalDateTime(startIso)} \u2013 ${formatLocalDateTime(endIso)}`;
  }
  const startDate = new Date(startIso);
  const endDate = new Date(endIso);
  const inclusiveEnd = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
  const startLabel = formatAllDayUtcDate(startDate);
  const endLabel = formatAllDayUtcDate(inclusiveEnd >= startDate ? inclusiveEnd : endDate);
  return startLabel === endLabel ? startLabel : `${startLabel} \u2013 ${endLabel}`;
}
